import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const OWNER_INBOX = "info@sureraddawithsanchayeeta.in";

const bookingSchema = z.object({
  event_name: z.string().trim().min(2).max(200),
  event_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  location: z.string().trim().min(2).max(300),
  contact_phone: z.string().trim().regex(/^[6-9]\d{9}$/),
  organiser_email: z.string().trim().toLowerCase().email().max(254),
  amount: z.number().min(0).max(10000000),
  upi_txn_id: z.string().trim().max(100).optional().or(z.literal("")),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
});

export const createBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => bookingSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("bookings").insert({
      user_id: userId,
      event_name: data.event_name,
      event_date: data.event_date,
      location: data.location,
      contact_phone: data.contact_phone,
      organiser_email: data.organiser_email,
      amount: data.amount,
      upi_txn_id: data.upi_txn_id || null,
      notes: data.notes || null,
    });
    if (error) throw new Error(error.message);

    // Best-effort: generate PDF receipt, upload to private storage, email links.
    try {
      const { sendTemplateEmail } = await import("@/lib/email/send-internal.server");
      const { generateAndUploadReceipt } = await import("@/lib/receipts/booking-receipt.server");
      const { SITE } = await import("@/config/site");
      const bookerEmail = (context.claims?.email as string | undefined) || undefined;

      const receiptNo = `SAS-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;

      let receiptUrl: string | null = null;
      try {
        const r = await generateAndUploadReceipt({
          userId,
          receiptNo,
          event_name: data.event_name,
          event_date: data.event_date,
          location: data.location,
          amount: data.amount,
          upi_txn_id: data.upi_txn_id || null,
          contact_phone: data.contact_phone,
          organiser_email: data.organiser_email,
          booker_email: bookerEmail,
          upi_payee_id: SITE.upi.id,
          upi_payee_name: SITE.upi.payeeName,
        });
        receiptUrl = r.signedUrl;
      } catch (e) {
        console.error("receipt generation failed:", e);
      }

      const confirmationData = {
        event_name: data.event_name,
        event_date: data.event_date,
        location: data.location,
        amount: data.amount,
        upi_txn_id: data.upi_txn_id || null,
        contact_phone: data.contact_phone,
        receipt_url: receiptUrl,
        receipt_no: receiptNo,
      };

      const notificationData = {
        ...confirmationData,
        organiser_email: data.organiser_email,
        booker_email: bookerEmail ?? "",
        notes: data.notes || null,
      };

      const sends: Array<Promise<unknown>> = [];

      if (bookerEmail) {
        sends.push(
          sendTemplateEmail({
            templateName: "booking-confirmation",
            recipientEmail: bookerEmail,
            templateData: confirmationData,
          }),
        );
      }

      sends.push(
        sendTemplateEmail({
          templateName: "booking-notification",
          recipientEmail: data.organiser_email,
          templateData: notificationData,
        }),
      );

      // Skip duplicate to owner if the organiser is the owner inbox.
      if (data.organiser_email !== OWNER_INBOX) {
        sends.push(
          sendTemplateEmail({
            templateName: "booking-notification",
            recipientEmail: OWNER_INBOX,
            templateData: notificationData,
          }),
        );
      }

      await Promise.allSettled(sends);
    } catch (e) {
      console.error("booking emails failed:", e);
    }

    return { ok: true };
  });

export const getMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("bookings")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const testBookingSchema = z.object({
  organiser_email: z.string().trim().toLowerCase().email().max(254),
});

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin only");
}

export const sendTestBookingEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => testBookingSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { sendTemplateEmail } = await import("@/lib/email/send-internal.server");
    const bookerEmail = (context.claims?.email as string | undefined) || "";
    const result = await sendTemplateEmail({
      templateName: "booking-notification",
      recipientEmail: data.organiser_email,
      idempotencyKey: `test-booking-${Date.now()}`,
      templateData: {
        event_name: "TEST · Sample booking",
        event_date: new Date().toISOString().slice(0, 10),
        location: "Sample venue, Kolkata",
        amount: 1000,
        upi_txn_id: "TEST-TXN-0000",
        contact_phone: "9999999999",
        organiser_email: data.organiser_email,
        booker_email: bookerEmail,
        notes: "This is a test notification triggered from the booking page.",
      },
    });
    return result;
  });

